import React, { useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { auth, EMAIL_VERIFICATION_ACS } from "../firebase";
import { useEmailVerificationPoll } from "../hooks/useEmailVerificationPoll";

export default function AuthForm(): JSX.Element {
  const { isVerified, startAutoPoll } = useEmailVerificationPoll();
  const [showVerifyPanel, setShowVerifyPanel] = useState(false);

  useEffect(() => {
    if (showVerifyPanel) {
      startAutoPoll(120000, 4000); // Poll i 2 min, var 4e sekund
    }
  }, [showVerifyPanel, startAutoPoll]);

  useEffect(() => {
    if (isVerified) {
      window.location.reload();
    }
  }, [isVerified]);

  const [mode, setMode] = useState<"login" | "signup" | "reset">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [password2, setPassword2] = useState("");
  const minLenOk = password.length >= 8;
  const passwordsMatch = password2.length > 0 && password === password2;
  const signupReady = !!email && minLenOk && passwordsMatch;

  const err = (e: any) => e?.message?.replace("Firebase:", "").trim();

  const doLogin = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      if (!user.emailVerified) {
        try {
          await sendEmailVerification(user, EMAIL_VERIFICATION_ACS);
        } catch (err) {
          console.warn("[auth] sendEmailVerification failed (login)", err);
        }
        setShowVerifyPanel(true);
        return;
      }
    } catch (e) {
      setMsg(err(e));
    } finally {
      setBusy(false);
    }
  };

  const doSignup = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      try {
        await sendEmailVerification(user, EMAIL_VERIFICATION_ACS);
      } catch (e) {
        console.warn("[signup] sendEmailVerification failed", e);
      }
      setMsg("Konto skapat. Vi har skickat ett verifieringsmail – verifiera och logga in.");
      setShowVerifyPanel(true);
    } catch (e) {
      setMsg(err(e));
    } finally {
      setBusy(false);
    }
  };

  async function doReset(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      await sendPasswordResetEmail(auth, email);
      setMsg("Återställningsmail skickat om adressen finns.");
    } catch (e) {
      setMsg(err(e));
    } finally {
      setBusy(false);
    }
  }

  async function resendVerifyLink(): Promise<void> {
    if (!auth.currentUser) {
      setMsg("Logga in först för att skicka verifieringslänk.");
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await sendEmailVerification(auth.currentUser, EMAIL_VERIFICATION_ACS);
      setMsg("Ny verifieringslänk skickad. Kolla din inkorg.");
    } catch (e) {
      setMsg(err(e));
    } finally {
      setBusy(false);
    }
  }

  const INTRO_KEY = "gw-auth-intro-dismissed";
  const [showIntro, setShowIntro] = useState(false);

  useEffect(() => {
    if (mode === "signup") {
      const dismissed = localStorage.getItem(INTRO_KEY) === "1";
      setShowIntro(!dismissed);
    } else {
      setShowIntro(false);
    }
  }, [mode]);

  return (
    <div className="login-screen">
      <div className="auth-wrap">
        <div className="gw-card login-card" style={{ flex: "1 1 360px", maxWidth: 420 }}>
          <h2 style={{ margin: 0 }}>
            {mode === "login" ? "Logga in" : mode === "signup" ? "Skapa konto" : "Återställ lösenord"}
          </h2>

          {showVerifyPanel && (
            <div className="gw-banner" style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Verifiera din e-post</div>
              <div style={{ opacity: 0.9 }}>
                Vi har skickat en länk till <strong>{email}</strong>. Låt det här fönstret vara öppet –
                sidan uppdateras automatiskt när du klickat på länken i mejlet.
                Om den andra fliken visar <em>“ogiltig länk”</em> är det normalt.
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                <button className="btn" type="button" onClick={resendVerifyLink} disabled={busy}>
                  Skicka om e-post
                </button>
                <a className="btn btn-outline" href="/verify">
                  Öppna verifieringssidan
                </a>
              </div>
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <label style={{ display: "block", marginBottom: 6 }}>E-post</label>
            <input
              className="gw-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="namn@exempel.se"
              autoComplete={mode === "signup" ? "email" : "username"}
            />
          </div>

          {mode !== "reset" && (
            <div style={{ marginTop: 12 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Lösenord</label>
              <input
                className="gw-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
              />
            </div>
          )}

          {mode === "signup" && (
            <div style={{ marginTop: 12 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Lösenord (igen)</label>
              <input
                className="gw-input"
                type="password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                aria-invalid={password2 ? !passwordsMatch : undefined}
              />
              {password2 && !passwordsMatch && (
                <div className="gw-banner gw-banner--warn" style={{ marginTop: 8 }}>
                  Lösenorden matchar inte.
                </div>
              )}
              {!minLenOk && password.length > 0 && (
                <div className="gw-banner gw-banner--warn" style={{ marginTop: 8 }}>
                  Lösenordet måste vara minst 8 tecken.
                </div>
              )}
            </div>
          )}

          {msg && (
            <div className="gw-banner gw-banner--warn" style={{ marginTop: 12 }}>
              {msg}
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={mode === "login" ? doLogin : mode === "signup" ? doSignup : doReset}
              disabled={busy || (mode === "signup" ? !signupReady : false)}
              style={{ width: "100%" }}
            >
              {mode === "login" ? "Logga in" : mode === "signup" ? "Skapa konto" : "Skicka återställningslänk"}
            </button>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
            {mode !== "login" ? (
              <button
                type="button"
                onClick={() => setMode("login")}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--primary)",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Har konto? Logga in
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setMode("signup")}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--primary)",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Skapa konto
              </button>
            )}

            {mode !== "reset" && (
              <button
                type="button"
                onClick={() => setMode("reset")}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--primary)",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Glömt lösenord?
              </button>
            )}
          </div>

          <div style={{ marginTop: 24, fontSize: 12, opacity: 0.8, textAlign: "center", lineHeight: 1.5 }}>
            © 2025 Patrik Junno. All rights reserved.{" "}
            <a href="/legal/license.html" target="_blank" rel="noreferrer">
              Licens / License
            </a>
          </div>
        </div>
      </div>

      {mode === "signup" && showIntro && (
        <div className="auth-overlay" role="dialog" aria-modal="true" aria-labelledby="authIntroTitle">
          <div className="auth-modal gw-card">
            <h3 id="authIntroTitle" style={{ marginTop: 0, marginBottom: 8 }}>Innan du börjar</h3>
            <p style={{ margin: "8px 0" }}>
              Fyll i dina uppgifter och låt registreringssidan stå öppen.
              När du klickar på länken i mejlet verifieras ditt konto och sidan uppdateras automatiskt.
            </p>
            <p style={{ margin: "8px 0" }}>
              Om du ser meddelandet <em>“ogiltig länk”</em> i fönstret för verifiering är allt i sin ordning –
              det beror på ett känt problem hos Google, och verifieringen fungerar ändå.
            </p>

            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
              <button type="button" className="btn btn-primary" autoFocus onClick={() => setShowIntro(false)}>
                Jag förstår
              </button>

              <label style={{ display: "inline-flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      localStorage.setItem(INTRO_KEY, "1");
                    } else {
                      localStorage.removeItem(INTRO_KEY);
                    }
                  }}
                />
                Visa inte igen
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
